/**
 * mymemo — 내 업무 메모·세번 메모·★ 세번·검색 결론을 한 그리드로(2026-10-02, 품목 관리를 흡수).
 *
 *  - 그리드: ★ · 세번(없으면 '업무') · 제목 · 태그 · 📎 · 수정일. 고르면 오른쪽에 상세(목록은 그대로).
 *  - 메모는 고치는 대로 자동 저장(0.8초 뒤). 첨부는 끌어 놓기·파일 선택·붙여넣기(캡처).
 *  - 상세는 옵시디안처럼 문서 한 장(2026-10-06): 제목·속성 줄(세번·태그·상태·감시)·본문을 고르면 바로 고친다.
 *    보기/수정·'메모 적기' 단계가 없다. ★만 단 세번도 적기 시작하면 그 세번의 메모가 된다.
 *  - ★ 는 기존 세번 즐겨찾기(watchlist) 그대로 — 변경고시 감시가 그 표를 읽는다.
 *  - 검색 결론(옛 품목 관리)은 줄로 보이고 '품목 열기'로 기존 작업공간 창을 연다.
 * 키: N 새 메모 · / 찾기 · ↑↓ 다음 메모 · Esc 상세 닫기.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FileText, Image as ImageIcon, Paperclip, Pin, Plus, Search, Star, Trash2, X, Download, ExternalLink, ChevronLeft } from "lucide-react";
import {
  ACCEPT, MAX_FILE_BYTES, STATUS, SUGGESTED_TAGS, buildRows, firstLine, tidyMemo, createMemo, deleteMemo, deleteMemoAttachment, fetchMemoAttachment,
  findHskMentions, formatBytes, formatHsk, listMemos, matchesQuery, memoLoggedIn, updateMemo, uploadMemoAttachment,
} from "./memoApi";
import { fetchWatchlist, toggleWatch, useWatchlist } from "./watchlistApi";
import { hskDetailUrl, openLink } from "./reqLinks";
import AgentInstall from "./AgentInstall";
import { publishMemoShare } from "./shareSnapshotStore";
import "./MymemoPage.css";

const FILTERS = [
  { id: "all", label: "전체" },
  { id: "pinned", label: "고정" },
  { id: "hs", label: "세번 연결" },
  { id: "work", label: "메모만" },
  { id: "star", label: "★ 감시" },
  { id: "changed", label: "변경 있음" },
  { id: "attach", label: "첨부 있음" },
];
const SAVE_DELAY = 800;

const when = (v) => String(v || "").slice(0, 10);
const shortWhen = (v) => {
  const d = when(v);
  return d.startsWith(String(new Date().getFullYear())) ? d.slice(5) : d.slice(2);
};

export default function MymemoPage({ active = true }) {
  const loggedIn = memoLoggedIn();
  const wl = useWatchlist();
  const [memos, setMemos] = useState(null);
  const [stars, setStars] = useState([]);
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [tag, setTag] = useState("");
  const [sort, setSort] = useState("updated");
  const [selectedKey, setSelectedKey] = useState(null);
  // 상세 칸을 새로 그릴 때만 바뀐다 — 새 메모가 처음 저장돼 key 가 draft→m123 으로 바뀌어도
  // 입력 중인 칸이 다시 그려지지(커서를 잃지) 않게.
  const [detailKey, setDetailKey] = useState(0);
  const select = useCallback((key) => { setSelectedKey(key); setDetailKey((k) => k + 1); }, []);
  const [draft, setDraft] = useState(null); // 새 메모(아직 저장 전)
  const searchRef = useRef(null);

  const reload = useCallback(async () => {
    if (!loggedIn) { setMemos([]); return; }
    try {
      setError("");
      const [m, s] = await Promise.all([
        listMemos(),
        fetchWatchlist(),
      ]);
      setMemos(m.items || []);
      setUsage(m.usage || null);
      setStars(s || []);
    } catch (e) {
      setError(e.message || "메모를 불러오지 못했습니다.");
      setMemos((prev) => prev || []);
    }
  }, [loggedIn]);

  useEffect(() => { reload(); }, [reload]);
  // ★ 를 다른 화면에서 켜고 끄면 목록도 따라온다.
  const starCount = wl.count;
  useEffect(() => { if (memos) fetchWatchlist().then(setStars).catch(() => {}); }, [starCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const changed = useMemo(() => new Set(stars.filter((s) => s.has_change).map((s) => s.code)), [stars]);
  const starred = useMemo(() => new Set(stars.map((s) => s.code)), [stars]);

  const allRows = useMemo(() => buildRows({ memos: memos || [], stars }), [memos, stars]);
  const tagCounts = useMemo(() => {
    const c = new Map();
    allRows.forEach((r) => r.tags.forEach((t) => c.set(t, (c.get(t) || 0) + 1)));
    const sorted = [...c.entries()].sort((a, b) => b[1] - a[1]);
    knownTags = sorted.map(([t]) => t).slice(0, 6);
    return sorted.slice(0, 8);
  }, [allRows]);

  const rows = useMemo(() => {
    const out = allRows.filter((r) => {
      if (filter === "hs" && !(r.source === "memo" && r.kind === "hs")) return false;
      if (filter === "work" && r.kind !== "work") return false;
      if (filter === "star" && !(r.hsk && starred.has(r.hsk))) return false;
      if (filter === "changed" && !(r.hsk && changed.has(r.hsk))) return false;
      if (filter === "attach" && r.attachments.length === 0) return false;
      if (filter === "pinned" && !r.pinned) return false;
      if (tag && !r.tags.includes(tag)) return false;
      return matchesQuery(r, query);
    });
    // 고정 메모는 늘 위
    const pin = (a, b) => Number(b.pinned) - Number(a.pinned);
    if (sort === "hsk") {
      out.sort((a, b) => pin(a, b) || (a.hsk || "~").localeCompare(b.hsk || "~") || String(b.updatedAt).localeCompare(String(a.updatedAt)));
    } else {
      out.sort((a, b) => pin(a, b) || String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")));
    }
    return out;
  }, [allRows, filter, tag, query, sort, starred, changed]);

  const selected = draft && selectedKey === "draft" ? draft : allRows.find((r) => r.key === selectedKey) || null;
  // 상단 [공유] 가 이 메모를 공유한다(2026-10-10) — 저장된 메모만(새로 쓰는 중인 메모는 저장된 뒤)
  const shareKey = selected && selected.source !== "draft"
    ? JSON.stringify([selected.key, selected.title, selected.body, selected.hsk, selected.fields?.requirement, selected.tags])
    : "";
  useEffect(() => {
    if (!shareKey) { publishMemoShare(null); return undefined; }
    const [, title, body, hsk, requirement, tags] = JSON.parse(shareKey);
    publishMemoShare({ type: "memo", title: title || "", body: body || "", code: hsk || "", requirement: requirement || "", tags: tags || [] });
    return undefined;
  }, [shareKey]);
  useEffect(() => () => publishMemoShare(null), []);
  const counts = useMemo(() => ({
    all: allRows.length,
    hs: allRows.filter((r) => r.source === "memo" && r.kind === "hs").length,
    work: allRows.filter((r) => r.kind === "work").length,
    star: allRows.filter((r) => r.hsk && starred.has(r.hsk)).length,
    changed: allRows.filter((r) => r.hsk && changed.has(r.hsk)).length,
    attach: allRows.filter((r) => r.attachments.length).length,
    pinned: allRows.filter((r) => r.pinned).length,
  }), [allRows, starred, changed]);

  const newMemo = useCallback((preset = {}) => {
    setDraft({ key: "draft", source: "draft", id: null, kind: preset.hsk ? "hs" : "work", hsk: preset.hsk || "",
      title: preset.title || "", body: "", tags: [], status: "", attachments: [], workspaceId: preset.workspaceId || null });
    select("draft");
  }, [select]);

  // 메모 저장 결과를 목록에 반영
  const applySaved = useCallback((saved, fromKey) => {
    setMemos((prev) => {
      const list = prev || [];
      const i = list.findIndex((m) => m.id === saved.id);
      return i < 0 ? [saved, ...list] : list.map((m) => (m.id === saved.id ? saved : m));
    });
    if (fromKey === "draft" || fromKey !== `m${saved.id}`) {
      setDraft(null);
      setSelectedKey(`m${saved.id}`);
    }
  }, []);

  const filesChanged = useCallback((id, files) => {
    setMemos((prev) => (prev || []).map((m) => (m.id === id ? { ...m, attachments: files, updated_at: new Date().toISOString() } : m)));
  }, []);

  const removeMemo = useCallback(async (row) => {
    if (row.source === "draft") { setDraft(null); select(null); return; }
    if (!row.id) return;
    try {
      await deleteMemo(row.id);
      setMemos((prev) => (prev || []).filter((m) => m.id !== row.id));
      select(null);
      reload();
    } catch (e) { setError(e.message); }
  }, [reload, select]);

  // 키보드: N 새 메모, / 찾기, ↑↓ 이동, Esc 닫기 — 입력 중에는 Esc 만
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target?.tagName || "") || e.target?.isContentEditable;
      if (e.key === "Escape") { if (typing) e.target.blur(); else select(null); return; }
      if (typing || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === "/") { e.preventDefault(); searchRef.current?.focus(); }
      else if (e.key === "n" || e.key === "N") { e.preventDefault(); newMemo(); }
      else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        if (!rows.length) return;
        e.preventDefault();
        const i = rows.findIndex((r) => r.key === selectedKey);
        const next = e.key === "ArrowDown" ? Math.min(rows.length - 1, i + 1) : Math.max(0, i < 0 ? 0 : i - 1);
        select(rows[next].key);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, rows, selectedKey, newMemo, select]);

  if (!loggedIn) {
    return <Shell><Empty>로그인하면 업무 메모와 첨부 파일을 보관할 수 있습니다.</Empty></Shell>;
  }

  return (
    <Shell>
      <div className="mm-head">
        <h1>mymemo</h1>
        <label className="mm-search">
          <Search size={14} />
          <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="제목·내용·세번·태그·첨부 파일명 찾기" aria-label="메모 찾기" data-testid="memo-search" />
          <span className="mm-key">/</span>
        </label>
        <AgentInstall />
        <button type="button" className="mm-new" onClick={() => newMemo()} data-testid="memo-new">
          <Plus size={14} /> 새 메모 <span className="mm-key mm-key-dark">N</span>
        </button>
      </div>

      <div className="mm-chips" role="toolbar" aria-label="메모 거르기">
        {FILTERS.map((f) => (counts[f.id] > 0 || f.id === "all") && (
          <button key={f.id} type="button" className={filter === f.id ? "on" : ""} onClick={() => setFilter(f.id)}>
            {f.label} <b>{counts[f.id]}</b>
          </button>
        ))}
        {tagCounts.map(([t]) => (
          <button key={t} type="button" className={`tag${tag === t ? " on" : ""}`} onClick={() => setTag(tag === t ? "" : t)}>#{t}</button>
        ))}
        {usage && (
          <span className="mm-usage" title="첨부 파일 보관 공간 · 파일 하나 25MB까지">
            첨부 {formatBytes(usage.used_bytes)} / {formatBytes(usage.quota_bytes)}
          </span>
        )}
      </div>

      <InstallTip />
      {error && <div className="mm-error">{error}</div>}

      <div className={`mm-split${selected ? " has-detail" : ""}`}>
        <div className="mm-list">
          {memos === null ? (
            <Empty>메모를 불러오는 중입니다.</Empty>
          ) : rows.length === 0 ? (
            allRows.length === 0 ? (
              <Empty>
                아직 메모가 없습니다. <b>새 메모</b>로 업무 메모를 적거나, 관세율표에서 세번의 ★를 누르면 여기에 모입니다.
              </Empty>
            ) : <Empty>조건에 맞는 메모가 없습니다.</Empty>
          ) : (
            <table className="mm-grid">
              <thead>
                <tr>
                  {/* 2026-10-10 사용자: 메모가 주인이고 세번은 그 메모에 달린 것 — 제목이 첫 칸, 세번은 태그와 같은 '연결' 칸 */}
                  <th>제목</th>
                  <th className="c-links"><button type="button" onClick={() => setSort("hsk")} className={sort === "hsk" ? "on" : ""}>세번·요건·태그</button></th>
                  <th className="c-att" aria-label="첨부" />
                  <th className="c-date"><button type="button" onClick={() => setSort("updated")} className={sort === "updated" ? "on" : ""}>수정일</button></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className={r.key === selectedKey ? "sel" : ""} onClick={() => { if (r.key !== selectedKey) select(r.key); if (draft && r.key !== "draft") setDraft(null); }}
                    data-testid="memo-row">
                    <td className="c-title">
                      {r.pinned && <Pin size={11} className="mm-pin" aria-label="고정" />}
                      <span className="t">{r.title || <span className="mm-faint">{r.source === "star" ? "★만 달아 둔 세번" : "제목 없음"}</span>}</span>
                      {r.hsk && changed.has(r.hsk) && <span className="mm-chg">변경</span>}
                    </td>
                    <td className="c-links">
                      {r.hsk && (
                        <span className="mm-hsk-chip" data-testid="memo-row-hsk">
                          {r.hsk.length >= 4 && (
                            <button type="button" className={`mm-star${starred.has(r.hsk) ? " on" : ""}`}
                              title={starred.has(r.hsk) ? "★ 끄기(변경고시 알림 끔)" : "★ 켜기(변경고시 알림)"}
                              onClick={(e) => { e.stopPropagation(); toggleWatch(r.hsk, r.title).then(() => fetchWatchlist().then(setStars)); }}>
                              <Star size={11} fill={starred.has(r.hsk) ? "currentColor" : "none"} />
                            </button>
                          )}
                          <span className="mm-code">{formatHsk(r.hsk)}</span>
                        </span>
                      )}
                      {r.fields?.requirement && (
                        <span className="mm-req-chip" title={r.fields.requirement} data-testid="memo-row-req">요건 · {r.fields.requirement}</span>
                      )}
                      {r.systemTags.map((t) => <span key={`s-${t}`} className="mm-tag sys">{t}</span>)}
                      {r.tags.slice(0, 3).map((t) => <span key={t} className="mm-tag">{t}</span>)}
                    </td>
                    <td className="c-att">{r.attachments.length > 0 && <span title={`첨부 ${r.attachments.length}개`}><Paperclip size={12} />{r.attachments.length > 1 ? r.attachments.length : ""}</span>}</td>
                    <td className="c-date">{shortWhen(r.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selected && (
          <div className="mm-detail">
            <MemoDetail
              key={detailKey}
              row={selected}
              starred={selected.hsk ? starred.has(selected.hsk) : false}
              changed={selected.hsk ? changed.has(selected.hsk) : false}
              onSaved={applySaved}
              onDelete={() => removeMemo(selected)}
              onClose={() => { select(null); if (selected.source === "draft") setDraft(null); }}
              onStar={() => toggleWatch(selected.hsk, selected.title).then(() => fetchWatchlist().then(setStars))}
              onWriteMemo={(preset) => newMemo(preset)}
              onUsage={setUsage}
              onFiles={filesChanged}
              onError={setError}
            />
          </div>
        )}
      </div>

    </Shell>
  );
}

function MemoDetail({ row, starred, changed, onSaved, onDelete, onClose, onStar, onWriteMemo, onUsage, onFiles, onError }) {
  const [hsk, setHsk] = useState(row.hsk ? formatHsk(row.hsk) : "");
  // 세번은 메모에 달리는 속성 하나 — 있을 때만 줄을 보이고, 없으면 '+ 세번 연결'로 단다(2026-10-10)
  const [hskAdded, setHskAdded] = useState(false);
  const showHsk = Boolean(hsk.trim()) || hskAdded;
  const [title, setTitle] = useState(row.source === "memo" ? row.memo?.title || "" : row.title || "");
  const [body, setBody] = useState(row.body || "");
  const [tags, setTags] = useState((row.tags || []).join(", "));
  const [status, setStatus] = useState(row.status || "");
  const [pinned, setPinned] = useState(Boolean(row.pinned));
  const [fields] = useState(row.fields || {}); // 업무 항목 — 입력 칸은 뺐고 이미 있는 값만 그대로 보관
  // 요건도 세번처럼 메모에 다는 속성 — 있을 때만 줄을 보이고, 없으면 '+ 요건 연결'(2026-10-10)
  const [requirement, setRequirement] = useState(row.fields?.requirement || "");
  const [reqAdded, setReqAdded] = useState(false);
  const showReq = Boolean(requirement.trim()) || reqAdded;
  const [saveState, setSaveState] = useState(row.id ? "saved" : "");
  const [files, setFiles] = useState(row.attachments || []);
  const [uploading, setUploading] = useState(0);
  const [viewer, setViewer] = useState(null); // {url, name, kind}
  const [confirmDel, setConfirmDel] = useState(false);
  const [tagFocus, setTagFocus] = useState(false);
  const idRef = useRef(row.id);
  const dirtyRef = useRef(false);
  const timerRef = useRef(null);
  const fileRef = useRef(null);

  const payload = useCallback(() => ({
    hsk: hsk.replace(/\D/g, "") || null,
    title: title.trim() || null,
    body,
    tags: tags.split(/[,，\s]+/).map((t) => t.trim()).filter(Boolean),
    status: status || null,
    pinned,
    fields: (() => { const f = { ...fields }; delete f.requirement; if (requirement.trim()) f.requirement = requirement.trim(); return f; })(),
    workspace_id: row.workspaceId || row.memo?.workspace_id || null,
  }), [hsk, title, body, tags, status, pinned, fields, requirement, row]);

  const save = useCallback(async () => {
    const p = payload();
    if (p.hsk && !/^\d{4}(\d{2}(\d{4})?)?$/.test(p.hsk)) { setSaveState("세번은 4·6·10자리"); return null; }
    if (!p.hsk && !p.title && !String(p.body || "").trim() && !Object.values(p.fields || {}).some(Boolean)) return null;
    setSaveState("저장 중");
    try {
      const saved = idRef.current ? await updateMemo(idRef.current, p) : await createMemo(p);
      const fromKey = idRef.current ? `m${idRef.current}` : row.source === "star" ? row.key : "draft";
      idRef.current = saved.id;
      dirtyRef.current = false;
      setSaveState("saved");
      onSaved(saved, fromKey);
      return saved.id;
    } catch (e) {
      setSaveState("저장 못 함");
      onError(e.message);
      return null;
    }
  }, [payload, onSaved, onError, row]);

  // 고치는 대로 0.8초 뒤 저장
  useEffect(() => {
    if (!dirtyRef.current) return undefined;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(save, SAVE_DELAY);
    return () => clearTimeout(timerRef.current);
  }, [hsk, title, body, tags, status, pinned, fields, requirement, save]);
  // 다른 메모로 옮길 때 저장 못 한 것이 있으면 바로 저장
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => () => { if (dirtyRef.current) saveRef.current(); }, []);

  const edit = (setter) => (e) => { dirtyRef.current = true; setSaveState("고침"); setter(e.target.value); };
  const touch = () => { dirtyRef.current = true; setSaveState("고침"); };
  const currentTags = tags.split(/[,，\s]+/).map((t) => t.trim()).filter(Boolean);
  const addTag = (t) => { if (currentTags.includes(t)) return; touch(); setTags([...currentTags, t].join(", ")); };

  const upload = useCallback(async (list) => {
    const items = [...list].filter(Boolean);
    if (!items.length) return;
    for (const f of items) {
      if (f.size > MAX_FILE_BYTES) { onError(`${f.name}: 파일 하나는 25MB 이하만 올릴 수 있습니다.`); return; }
    }
    let id = idRef.current;
    if (!id) {
      if (!title.trim() && !body.trim() && !hsk.trim()) { setTitle(items[0].name); }
      dirtyRef.current = true;
      id = await saveRef.current();
      if (!id) {
        // 제목이 아직 비어 저장되지 않았다면 파일 이름으로 한 번 더
        const saved = await createMemo({ ...payload(), title: title.trim() || items[0].name }).catch((e) => { onError(e.message); return null; });
        if (!saved) return;
        idRef.current = saved.id;
        id = saved.id;
        onSaved(saved, "draft");
      }
    }
    setUploading((n) => n + items.length);
    for (const f of items) {
      try {
        const a = await uploadMemoAttachment(id, f);
        setFiles((prev) => { const next = [...prev, a]; onFiles(id, next); return next; });
        if (a.usage) onUsage(a.usage);
      } catch (e) {
        onError(`${f.name}: ${e.message}`);
      } finally {
        setUploading((n) => n - 1);
      }
    }
  }, [body, hsk, title, payload, onError, onSaved, onUsage, onFiles]);

  const onPaste = (e) => {
    const pasted = [...(e.clipboardData?.files || [])];
    if (pasted.length) {
      e.preventDefault();
      upload(pasted.map((f, i) => (f.name && f.name !== "image.png" ? f
        : new File([f], `캡처_${new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "")}${i ? `_${i}` : ""}.png`, { type: f.type }))));
    }
  };

  const openFile = async (a, download) => {
    try {
      const url = await fetchMemoAttachment(idRef.current, a.id);
      const ext = String(a.file_name).split(".").pop().toLowerCase();
      const kind = ext === "pdf" ? "pdf" : /^(png|jpe?g|webp|gif|bmp)$/.test(ext) ? "image" : null;
      if (download || !kind) {
        const link = document.createElement("a");
        link.href = url;
        link.download = a.file_name;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      } else {
        setViewer({ url, name: a.file_name, kind });
      }
    } catch (e) { onError(e.message); }
  };

  const removeFile = async (a) => {
    try {
      const r = await deleteMemoAttachment(idRef.current, a.id);
      const next = files.filter((x) => x.id !== a.id);
      setFiles(next);
      onFiles(idRef.current, next);
      if (r.usage) onUsage(r.usage);
    } catch (e) { onError(e.message); }
  };

  useEffect(() => () => { if (viewer?.url) URL.revokeObjectURL(viewer.url); }, [viewer]);

  const code = hsk.replace(/\D/g, "");
  const currentStatus = status && STATUS[status];
  const files_ = (
    <div className="mm-files">
      {files.map((a) => (
        <div key={a.id} className="mm-file">
          <span className="ico">{/\.pdf$/i.test(a.file_name) ? <span className="pdf">PDF</span> : /\.(png|jpe?g|webp|gif|bmp)$/i.test(a.file_name) ? <ImageIcon size={14} /> : <FileText size={14} />}</span>
          <button type="button" className="name" onClick={() => openFile(a, false)} title="열기">{a.file_name}</button>
          <span className="size">{formatBytes(a.size)}</span>
          <button type="button" className="mm-icon" title="내려받기" onClick={() => openFile(a, true)}><Download size={13} /></button>
          <button type="button" className="mm-icon" title="첨부 지우기" onClick={() => removeFile(a)}><X size={13} /></button>
        </div>
      ))}
      {uploading > 0 && <div className="mm-file mm-faint">올리는 중… {uploading}개</div>}
      <button type="button" className="mm-drop" onClick={() => fileRef.current?.click()} title="PDF·이미지·한글·엑셀 · 25MB까지 · Ctrl+V 로 캡처 붙여넣기">
        <Paperclip size={13} /> 파일을 끌어 놓거나 눌러서 올리기
      </button>
      <input ref={fileRef} type="file" multiple accept={ACCEPT} hidden onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
    </div>
  );
  return (
    <div className="mm-d mm-doc" onPaste={onPaste}
      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("drag"); }}
      onDragLeave={(e) => e.currentTarget.classList.remove("drag")}
      onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("drag"); upload(e.dataTransfer.files); }}>
      <div className="mm-d-top">
        <button type="button" className="mm-back" onClick={onClose} aria-label="목록으로"><ChevronLeft size={16} /></button>
        <span className="mm-d-kind">
          {code ? "세번 메모" : "업무 메모"}
          {saveState && <span className="mm-save" data-testid="memo-save-state">{saveState === "saved" ? "저장됨" : saveState === "고침" ? "저장 중…" : saveState}</span>}
        </span>
        <span className="mm-d-actions">
          <button type="button" className={`mm-icon${pinned ? " on" : ""}`} title={pinned ? "고정 풀기" : "맨 위에 고정"}
            onClick={() => { touch(); setPinned((v) => !v); }} aria-pressed={pinned} data-testid="memo-pin">
            <Pin size={14} />
          </button>
          {(idRef.current || row.source === "draft") && (confirmDel ? (
            <span className="mm-confirm">지울까요? <button type="button" onClick={onDelete}>지우기</button><button type="button" onClick={() => setConfirmDel(false)}>그만두기</button></span>
          ) : (
            <button type="button" className="mm-icon" title="메모 지우기" onClick={() => setConfirmDel(true)}><Trash2 size={14} /></button>
          ))}
          <button type="button" className="mm-icon" title="닫기 (Esc)" onClick={onClose}><X size={15} /></button>
        </span>
      </div>

      {/* 옵시디안처럼 — 고르면 바로 적고, 적는 대로 저장한다(2026-10-06 사용자 결정: '메모 적기'·보기/수정 없이) */}
      <input className="mm-doc-title" value={title} onChange={edit(setTitle)} placeholder={firstLine(body) || "제목 없음"}
        aria-label="제목" autoFocus={row.source === "draft"} data-testid="memo-title" />

      <div className="mm-props" data-testid="memo-props">
        {showHsk
          ? <HskProp value={hsk} onChange={edit(setHsk)} code={code} name={row.source === "star" ? row.title : ""} autoFocus={hskAdded} />
          : null}
        {showReq && (
          <div className="mm-prop" data-testid="memo-req">
            <span className="mm-prop-k">요건</span>
            <span className="mm-prop-v">
              <input value={requirement} onChange={edit(setRequirement)} maxLength={200} autoFocus={reqAdded}
                placeholder="예: 식품위생법 수입신고 · 전파법 적합성평가" aria-label="요건" />
            </span>
          </div>
        )}
        <div className="mm-prop">
          <span className="mm-prop-k">태그</span>
          <span className="mm-prop-v">
            <input value={tags} onChange={edit(setTags)} placeholder="비어 있음" aria-label="태그"
              onFocus={() => setTagFocus(true)} onBlur={() => setTagFocus(false)} />
          </span>
        </div>
        {tagFocus && <TagSuggest current={currentTags} onAdd={addTag} />}
        <div className="mm-prop">
          <span className="mm-prop-k">상태</span>
          <span className="mm-prop-v">
            <select value={status} onChange={edit(setStatus)} aria-label="상태" className={currentStatus ? "" : "empty"}>
              <option value="">비어 있음</option>
              <option value="reviewing">검토 중</option>
              <option value="conditional">추가 확인</option>
              <option value="confirmed">확정</option>
            </select>
          </span>
        </div>
        {code.length >= 4 && (
          <div className="mm-prop">
            <span className="mm-prop-k">감시</span>
            <span className="mm-prop-v">
              <button type="button" className={`mm-watch${starred ? " on" : ""}`} onClick={onStar} data-testid="memo-watch">
                <Star size={13} fill={starred ? "currentColor" : "none"} /> {starred ? "세율·요건이 바뀌면 알림" : "알리지 않음"}
              </button>
            </span>
          </div>
        )}
        {row.updatedAt && (
          <div className="mm-prop">
            <span className="mm-prop-k">수정</span>
            <span className="mm-prop-v mm-faint">{when(row.updatedAt)} · 자동 저장</span>
          </div>
        )}
        {(!showHsk || !showReq) && (
          <div className="mm-prop-adds">
            {!showHsk && <button type="button" className="mm-prop-add" onClick={() => setHskAdded(true)} data-testid="memo-add-hsk">+ 세번 연결</button>}
            {!showReq && <button type="button" className="mm-prop-add" onClick={() => setReqAdded(true)} data-testid="memo-add-req">+ 요건 연결</button>}
          </div>
        )}
      </div>
      {changed && <div className="mm-note">이 세번의 세율·요건이 바뀌었습니다. <a href={hskDetailUrl(code)} onClick={(e) => { e.preventDefault(); openLink(hskDetailUrl(code)); }}>세번 상세 보기</a></div>}

      <hr className="mm-doc-sep" />
      <DocBody body={body} onChange={(v) => { dirtyRef.current = true; setSaveState("고침"); setBody(v); }} />
      {body.trim() && <Tidy body={body} onApply={(next) => { dirtyRef.current = true; setSaveState("고침"); setBody(next); }} onError={onError} />}
      {files_}

      {viewer && (
        <div className="mm-viewer" role="dialog" aria-label={viewer.name}>
          <div className="mm-viewer-bar">
            <span>{viewer.name}</span>
            <a href={viewer.url} target="_blank" rel="noreferrer">새 창 <ExternalLink size={12} /></a>
            <button type="button" className="mm-icon" onClick={() => setViewer(null)} aria-label="닫기"><X size={16} /></button>
          </div>
          {viewer.kind === "pdf"
            ? <iframe title={viewer.name} src={viewer.url} />
            : <div className="mm-viewer-img"><img src={viewer.url} alt={viewer.name} /></div>}
        </div>
      )}
    </div>
  );
}

/** 세번 속성 줄 — 평소엔 글처럼, 마우스를 올리면 세율 요약·세번 상세(10자리면 세율까지). */
const hskSummaryCache = new Map();
function HskProp({ value, onChange, code, name, autoFocus = false }) {
  const [info, setInfo] = useState(() => hskSummaryCache.get(code) || null);
  const load = () => {
    if (code.length !== 10 || hskSummaryCache.has(code)) { if (hskSummaryCache.has(code)) setInfo(hskSummaryCache.get(code)); return; }
    hskSummaryCache.set(code, { loading: true });
    fetch(`/api/onto/hsk-detail?hsk=${code}&lang=kor`).then((r) => (r.ok ? r.json() : null)).catch(() => null).then((d) => {
      // 기본세율(A)은 rates_grouped.basic 에 따로 있다(general 에는 C·E·R·U)
      const gen = [...(d?.rates_grouped?.basic || []), ...(d?.rates_grouped?.general || [])];
      const pick = (re) => gen.find((x) => re.test(String(x.code || "")));
      const fta = Object.values(d?.rates_grouped?.fta || {}).flat().filter((x) => typeof x.value === "number").sort((a, b) => a.value - b.value)[0];
      const v = d ? { name: String(d.name_ko || "").replace(/\/양허유형;\d+$/, ""), base: pick(/^A/)?.raw, wto: pick(/^C/)?.raw, fta: fta ? `${fta.raw || `${fta.value}%`} (${fta.code})` : null } : { failed: true };
      hskSummaryCache.set(code, v);
      setInfo(v);
    });
  };
  const detail = hskDetailUrl(code);
  return (
    <div className="mm-prop mm-prop-hsk" onMouseEnter={load} onFocus={load}>
      <span className="mm-prop-k">세번</span>
      <span className="mm-prop-v">
        <input value={value} onChange={onChange} placeholder="4·6·10자리" aria-label="세번" inputMode="numeric" className="mm-code" autoFocus={autoFocus} />
        {(name || info?.name) && <span className="mm-prop-sub">{name || info.name}</span>}
      </span>
      {code.length >= 4 && (
        <div className="mm-pop" role="tooltip" data-testid="memo-hsk-pop">
          <b className="mm-code">{formatHsk(code)}</b>
          {(info?.name || name) && <div className="mm-pop-name">{info?.name || name}</div>}
          {info && !info.loading && !info.failed && (
            <dl>
              {info.base && <><dt>기본세율</dt><dd>{info.base}</dd></>}
              {info.wto && <><dt>WTO 협정세율</dt><dd>{info.wto}</dd></>}
              {info.fta && <><dt>FTA 최저</dt><dd>{info.fta}</dd></>}
            </dl>
          )}
          {info?.loading && <div className="mm-faint">세율 불러오는 중…</div>}
          <a href={detail} onClick={(e) => { e.preventDefault(); openLink(detail); }}>세번 상세 <ExternalLink size={11} /></a>
        </div>
      )}
    </div>
  );
}

/** 본문 — 평소엔 링크·세번을 누를 수 있는 글로 보이고, 글을 누르면 그 자리에서 바로 고친다(따로 수정 단계 없음). */
function DocBody({ body, onChange }) {
  const [focused, setFocused] = useState(!body.trim());
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.max(220, el.scrollHeight)}px`;
  }, [body, focused]);
  if (!focused && body.trim()) {
    return (
      <div className="mm-doc-body view" data-testid="memo-body-view" tabIndex={0}
        onClick={(e) => { if (!e.target.closest("a")) setFocused(true); }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setFocused(true); } }}>
        <RichText text={body} />
      </div>
    );
  }
  return (
    <textarea ref={ref} className="mm-doc-body" value={body} onChange={(e) => onChange(e.target.value)} data-testid="memo-body"
      autoFocus={Boolean(body.trim())} onBlur={() => setFocused(false)}
      placeholder="여기에 바로 적으세요. 세번(예: 8479.89)·링크(https://…)는 다 적고 나면 눌러서 열 수 있습니다. 파일은 끌어다 놓으면 붙습니다." />
  );
}

/** 태그 고르기 — 이미 쓴 것은 빼고, 자주 쓰는 통관 업무 분류를 한 번 눌러 더한다. */
let knownTags = [];
function TagSuggest({ current, onAdd }) {
  const list = [...new Set([...knownTags, ...SUGGESTED_TAGS])].filter((t) => !current.includes(t)).slice(0, 10);
  if (!list.length) return null;
  return (
    <div className="mm-suggest" aria-label="태그 고르기">
      {list.map((t) => <button key={t} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => onAdd(t)}>+ {t}</button>)}
    </div>
  );
}

/** 보기 화면 본문 — 줄바꿈 그대로, 링크(http/https)와 세번은 글 안에서 바로 누른다. */
function RichText({ text }) {
  const parts = [];
  const re = /https?:\/\/[^\s<>"'()]+/g;
  let last = 0;
  let m;
  const s = String(text || "");
  const pushText = (chunk, key) => {
    let at = 0;
    findHskMentions(chunk).forEach((h, i) => {
      if (h.start > at) parts.push(chunk.slice(at, h.start));
      const url = hskDetailUrl(h.code);
      parts.push(<a key={`${key}-h${i}`} className="mm-inline-code" href={url} onClick={(e) => { e.preventDefault(); openLink(url); }}>{chunk.slice(h.start, h.end)}</a>);
      at = h.end;
    });
    if (at < chunk.length) parts.push(chunk.slice(at));
  };
  while ((m = re.exec(s))) {
    const u = m[0].replace(/[.,;:!?)\]]+$/, "");
    if (m.index > last) pushText(s.slice(last, m.index), `t${last}`);
    parts.push(<a key={`u${m.index}`} href={u} target="_blank" rel="noreferrer noopener">{u}</a>);
    last = m.index + u.length;
  }
  if (last < s.length) pushText(s.slice(last), `t${last}`);
  return <>{parts}</>;
}


/** AI로 정리 — 적은 메모를 요약 한두 줄 + 정리된 본문으로. 저장하지 않고, 바꿀지는 사람이 고른다. */
function Tidy({ body, onApply, onError }) {
  const [state, setState] = useState(null); // null | "busy" | {summary, organized}
  const enough = String(body || "").trim().length >= 20;
  const run = async () => {
    setState("busy");
    try { setState(await tidyMemo(body)); }
    catch (e) { setState(null); onError(e.message); }
  };
  if (state && state !== "busy") {
    const merged = (state.summary ? `요약: ${state.summary}\n\n` : "") + state.organized;
    return (
      <div className="mm-tidy" data-testid="memo-tidy-result">
        <div className="mm-tidy-head">AI가 정리한 글 <span className="mm-faint">— 숫자·세번·신고번호는 그대로 두라고 했지만 한 번 확인해 주세요</span></div>
        {state.summary && <div className="mm-tidy-sum">{state.summary}</div>}
        <pre className="mm-tidy-body">{state.organized}</pre>
        <div className="mm-btns">
          <button type="button" className="mm-new" onClick={() => { onApply(merged); setState(null); }}>이 글로 바꾸기</button>
          <button type="button" className="mm-plain" onClick={() => { onApply(`${merged}\n\n── 원래 메모 ──\n${body}`); setState(null); }}>원래 메모는 아래에 두기</button>
          <button type="button" className="mm-plain" onClick={() => setState(null)}>닫기</button>
        </div>
      </div>
    );
  }
  return (
    <div className="mm-tidy-bar">
      <button type="button" className="mm-plain" disabled={!enough || state === "busy"} onClick={run} data-testid="memo-tidy"
        title={enough ? "적은 메모를 요약하고 읽기 좋게 다듬습니다" : "조금 더 적으면 정리할 수 있습니다"}>
        {state === "busy" ? "정리하는 중…" : "AI로 정리"}
      </button>
    </div>
  );
}


const TIP_KEY = "loginhs:mm-install-tip";
/** 휴대폰 브라우저에서만(CSS 로 좁은 화면만 보임) — 설치 앱으로 열었거나 닫았으면 안 보인다. */
function InstallTip() {
  const [hidden, setHidden] = useState(() => {
    try {
      if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) return true;
      return localStorage.getItem(TIP_KEY) === "1";
    } catch { return false; }
  });
  if (hidden) return null;
  return (
    <div className="mm-install-tip">
      <span>홈 화면에 설치하면 앱처럼 바로 열립니다 · <a href="/?page=install">설치 안내</a></span>
      <button type="button" aria-label="닫기" onClick={() => { setHidden(true); try { localStorage.setItem(TIP_KEY, "1"); } catch { /* 무시 */ } }}><X size={14} /></button>
    </div>
  );
}

function Shell({ children }) {
  return <div className="mymemo audit-fade-in">{children}</div>;
}

function Empty({ children }) {
  return <div className="mm-empty">{children}</div>;
}
